import {
  JsonApiArrayRelationshipDefinition,
  JsonApiAttributes,
  JsonApiListResponse,
  JsonApiRelationships,
  JsonApiResourceDto,
  JsonApiResourceIdentifier,
  JsonApiSingleRelationshipDefinition,
} from '../network/types';
import logger from '../logging/logger';
import {
  RevisionedDocument,
  DocumentPageType,
  DocumentPageImageType,
  DocumentProcessingStatus,
  AccessRules,
  DocumentAcl,
  FieldRules,
  FieldPermissions,
  ReadinessInfo,
} from '../types/document';
import {
  FieldPermission,
  FieldType,
  DefaultFieldRules,
  AllFieldPermissions,
} from '../constants';

interface DocumentResourceAttributes extends JsonApiAttributes {
  name: string;
  createdDate: string;
  expiryDate: string;
  readinessInfo?: ReadinessInfo;
}
interface DocumentResourceRelationships extends JsonApiRelationships {
  latestRevision: JsonApiSingleRelationshipDefinition;
  latestProgress: JsonApiSingleRelationshipDefinition;
  pages: JsonApiArrayRelationshipDefinition;
  processingStatus: JsonApiSingleRelationshipDefinition;
  documentAcl: JsonApiSingleRelationshipDefinition;
}

interface DocumentPageAttributes extends JsonApiAttributes {
  documentId: string;
  width: number;
  height: number;
  pageNumber: number;
  hasContent: boolean;
  hasLinkContent?: boolean;
  hasFieldContent?: boolean;
}
interface DocumentPageRelationships extends JsonApiRelationships {
  pageImage: JsonApiSingleRelationshipDefinition;
}
type DocumentPageResource = JsonApiResourceDto<
  DocumentPageAttributes,
  DocumentPageRelationships,
  void
>;

interface ProcessingStatusAttributes extends JsonApiAttributes {
  inProgress: boolean;
}

type ProcessingStatusResource = JsonApiResourceDto<
  ProcessingStatusAttributes,
  undefined,
  void
>;

interface DocumentAclAttributes extends JsonApiAttributes {
  accessRules: AccessRules;
}

export type DocumentAclResource = JsonApiResourceDto<
  DocumentAclAttributes,
  undefined,
  void
>;

export type IncludedDataMap = {
  [resourceType: string]: {
    [resourceId: string]: any;
  };
};

function normalizePageImage(
  relationshipData: JsonApiResourceIdentifier,
  includedDataMap: IncludedDataMap
): DocumentPageImageType {
  const {
    id,
    attributes: { url, pageNumber, expiresAt },
  } = includedDataMap.documentPageImages[relationshipData.id];

  return {
    id,
    url,
    pageNumber,
    expiresAt,
  };
}

function normalizeDocumentPages(
  relationshipData: Array<JsonApiResourceIdentifier>,
  includedDataMap: IncludedDataMap
): Array<DocumentPageType> {
  const pages = relationshipData.reduce((pages, pageRelationship) => {
    const pageResource = includedDataMap.documentPages[pageRelationship.id];

    if (!pageResource) {
      logger.error(`Page resource not found! Page "${pageRelationship.id}"`);
    } else {
      const {
        id,
        attributes: {
          documentId,
          pageNumber,
          width,
          height,
          scaleFactor,
          hasContent,
          hasLinkContent,
          hasFieldContent,
        },
        relationships: { pageImage },
      } = pageResource as DocumentPageResource;
      const page: DocumentPageType = {
        id,
        documentId,
        pageNumber,
        width,
        height,
        scaleFactor,
        hasContent,
        hasLinkContent,
        hasFieldContent,
        pageImage: normalizePageImage(pageImage.data, includedDataMap),
      };

      pages.push(page);
    }

    return pages;
  }, [] as Array<DocumentPageType>);

  return pages.sort((page1, page2) => page1.pageNumber - page2.pageNumber);
}

function normalizeProcessingStatus(
  relationshipData: JsonApiResourceIdentifier,
  includedDataMap: IncludedDataMap
): DocumentProcessingStatus {
  const {
    id,
    attributes: { inProgress },
  } = includedDataMap.processingStatus[
    relationshipData.id
  ] as ProcessingStatusResource;

  return {
    id,
    inProgress,
  };
}

const basicFieldPermissions = [
  FieldPermission.ADD,
  FieldPermission.REMOVE,
  FieldPermission.POSITION,
];
const basicEditableFieldPermissions = basicFieldPermissions.concat(
  FieldPermission.VALUE
);
const allFieldPermissions = Object.values(
  FieldPermission
) as Array<FieldPermission>;
const nonLinkableFieldPermissions = allFieldPermissions.filter(
  p => p !== FieldPermission.LINKED_FIELDS
);
const allFieldTypes = Object.values(FieldType) as Array<FieldType>;

type DenormalizedFieldRulesKey =
  | typeof DefaultFieldRules
  | Exclude<FieldType, FieldType.DateTime | FieldType.Time>;

type DenormalizedFieldPermission = typeof AllFieldPermissions | FieldPermission;

type DenormalizedFieldRules = {
  [key in DenormalizedFieldRulesKey]?: Array<DenormalizedFieldPermission>;
};

type DenormalizedAccessRules = {
  fields: DenormalizedFieldRules;
};

// Every field type has the concept of ALL permissions however the meaning of ALL differs by field type
function normalizeFullAccessForFieldType(fieldType: FieldType) {
  switch (fieldType) {
    case FieldType.Signature:
    case FieldType.Initial:
      return allFieldPermissions;
    case FieldType.Note:
      return basicEditableFieldPermissions;
    case FieldType.Image:
    case FieldType.Strike:
      return basicFieldPermissions;
  }

  return nonLinkableFieldPermissions;
}

function normalizeEffectiveFieldPermissionsForFieldType(
  fieldType: FieldType,
  fieldPermission: FieldPermission
) {
  if (
    fieldType === FieldType.Note &&
    fieldPermission === FieldPermission.VALUE
  ) {
    // Notes do not have a concept of HELP but the value is stored in the helpText property
    // so to aid in permission validation the VALUE permission is being mapped to HELP for Notes
    return FieldPermission.HELP;
  }

  return fieldPermission;
}

function normalizeFieldPermissions(
  fieldType: FieldType,
  fieldPermissions: Array<DenormalizedFieldPermission>
) {
  const hasFullAccess = fieldPermissions.includes(AllFieldPermissions);
  const effectivePermissions = (
    hasFullAccess
      ? normalizeFullAccessForFieldType(fieldType)
      : fieldPermissions
  ) as Array<FieldPermission>;
  const resultPermissions = allFieldPermissions.reduce(
    (result, fieldPermission) => {
      result[fieldPermission] = false;
      return result;
    },
    {} as FieldPermissions
  );
  return effectivePermissions.reduce((result, fieldPermission) => {
    const effectivePermission = normalizeEffectiveFieldPermissionsForFieldType(
      fieldType,
      fieldPermission
    );
    result[effectivePermission] = true;
    return result;
  }, resultPermissions);
}

function normalizeFieldRuleTypeForFieldType(fieldType: FieldType) {
  if (fieldType === FieldType.DateTime || fieldType === FieldType.Time) {
    return FieldType.Date;
  }

  return fieldType;
}

function normalizeFieldRules(fields: DenormalizedFieldRules): FieldRules {
  const defaultFieldPermissions = fields[DefaultFieldRules] ?? [];
  return allFieldTypes.reduce((fieldRules, fieldType) => {
    const fieldRule = normalizeFieldRuleTypeForFieldType(fieldType);
    const fieldPermissions = fields[fieldRule];
    fieldRules[fieldType] = fieldPermissions
      ? normalizeFieldPermissions(fieldType, fieldPermissions)
      : normalizeFieldPermissions(fieldType, defaultFieldPermissions);
    return fieldRules;
  }, {} as FieldRules);
}

function normalizeAccessRules(
  accessRules: DenormalizedAccessRules
): AccessRules {
  const { fields } = accessRules;
  return {
    fields: normalizeFieldRules(fields),
  };
}

function normalizeDocumentAcl(
  relationshipData: JsonApiResourceIdentifier,
  includedDataMap: IncludedDataMap
): DocumentAcl {
  const {
    id,
    attributes: { accessRules },
  } = includedDataMap.documentAcls[relationshipData.id] as DocumentAclResource;
  const normalizedAccessRules = accessRules
    ? normalizeAccessRules(accessRules as unknown as DenormalizedAccessRules)
    : undefined;
  return {
    id,
    accessRules: normalizedAccessRules,
  };
}

export function normalizeDocuments<R, P>(
  payload: JsonApiListResponse<
    DocumentResourceAttributes,
    DocumentResourceRelationships,
    void
  >,
  normalizeLatestRevision?: (
    relationshipData: JsonApiResourceIdentifier,
    includedDataMap: IncludedDataMap
  ) => R,
  normalizeLatestProgress?: (
    relationshipData: JsonApiResourceIdentifier,
    includedDataMap: IncludedDataMap
  ) => P
): Array<RevisionedDocument<R>> {
  const { data, included } = payload;
  const includedDataMap: IncludedDataMap =
    included?.reduce(
      (
        dataMap: IncludedDataMap,
        resource: JsonApiResourceDto<any, any, any>
      ) => {
        const { id, type } = resource;

        if (!dataMap[type]) {
          dataMap[type] = {};
        }

        dataMap[type][id] = resource;

        return dataMap;
      },
      {}
    ) ?? {};

  const normalizedDocuments = data.map(document => {
    const {
      id,
      attributes,
      relationships: {
        pages,
        latestRevision,
        latestProgress,
        processingStatus,
        documentAcl,
      },
    } = document;
    return {
      id,
      ...attributes,
      latestRevision:
        !!latestRevision && !!normalizeLatestRevision
          ? normalizeLatestRevision(latestRevision.data, includedDataMap)
          : undefined,
      latestProgress:
        !!latestProgress && !!normalizeLatestProgress
          ? normalizeLatestProgress(latestProgress.data, includedDataMap)
          : undefined,
      pages: pages ? normalizeDocumentPages(pages.data, includedDataMap) : [],
      processingStatus: processingStatus
        ? normalizeProcessingStatus(processingStatus.data, includedDataMap)
        : undefined,
      acl: documentAcl
        ? normalizeDocumentAcl(documentAcl.data, includedDataMap)
        : undefined,
    };
  });

  return normalizedDocuments;
}
